<template>
  <ModalComponent class="flatfinder-settings" :show="show" @close="onClose">
    <Card theme="flat" tag="form" @submit.prevent="onSubmit" class="flatfinder-settings__modal">
      <template #default>
        <h2>{{ $t('settings') }}</h2>
        <div class="col g-2">
          <InputComponent :label="$tc('changeFlatfinderReference')" required>
            <ResourceSelect
              class="elder-input__element"
              v-model="reference"
              @input="onReferenceInput"
              :items="resources.fetch"
              :is-disabled="resources.isDisabled"
              ref="resource"
              search
            />
          </InputComponent>

          <InputComponent
            v-if="['Link'].includes($path('reference.onModel', form))"
            v-model="link.label"
            required
            :label="$t('name')"
          />

          <MediaComponent
            v-if="['Link'].includes($path('reference.onModel', form))"
            v-model="form.data.image"
            :label="$tc('image', 1)"
            size="cover"
            :enableDescription="false"
            :enableAlt="false"
          />

          <InputComponent :label="$t('description')">
            <RichText v-model="form.data.description" :commands="['bold', 'italic']" />
          </InputComponent>

          <InputComponent
            v-if="['Link'].includes($path('reference.onModel', form))"
            v-model="link.href"
            required
            :label="$tc('link')"
          />

          <InputComponent
            v-if="$path('reference.onModel', form) && !['Link'].includes($path('reference.onModel', form))"
            :label="$tc('redirectUrl')"
            v-model="form.data.redirectUrl"
          />
          <InputComponent
            v-if="$path('reference.onModel', form) === 'Residential'"
            :label="$t('flatfinderThumbnailType')"
          >
            <SelectComponent v-model="form.data.thumbnailType">
              <option value="cover">{{ $t('cover') }}</option>
              <option value="floorplan">{{ $tc('floorplan') }}</option>
            </SelectComponent>
          </InputComponent>
        </div>
      </template>
      <template #footer>
        <div class="row g-2 end flex-1">
          <ButtonComponent :label="$t('cancel')" :icon="['fal', 'times']" @click="onClose" />
          <ButtonComponent
            :icon="['fal', 'angle-right']"
            type="submit"
            theme="primary"
            :promise="promise"
            :label="$t('confirm')"
            :disabled="!isValid"
          />
        </div>
      </template>
    </Card>
  </ModalComponent>
</template>

<script>
import { mergeDeepRight } from 'ramda'
import { ModalComponent } from 'vue-elder-modal'
import { InputComponent } from 'vue-elder-input'
import { MediaComponent } from '@kvass/media'
import RichText from '@kvass/rich-text'
import Card from '@/components/Card'
import SelectComponent from '@/components/Select/Select'
import ResourceSelect from '@/components/Flatfinder/Resources/Select/Modal'
import fetch from '@/fetch'

const DataTemplate = {
  reference: null,
  data: {
    description: null,
    redirectUrl: null,
    thumbnailType: 'cover',
    image: null,
  },
}

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    selected: Object,
    resources: Object,
  },
  watch: {
    show: {
      handler(val) {
        if (!val) return
        this.setInitialData()
      },
      immediate: true,
    },
  },
  data() {
    return {
      promise: null,
      form: structuredClone(DataTemplate),
      link: { label: null, href: null },
    }
  },
  computed: {
    reference: {
      get() {
        if (!this.form.reference) return
        return { onModel: this.form.reference.onModel, ...this.form.reference.ref }
      },
      set(val) {
        if (!val) return (this.form.reference = null)
        let { onModel, ...ref } = val

        this.form.reference = { onModel, ref }
      },
    },
    isValid() {
      const checks = [Boolean(this.reference)]

      if (this.$path('reference.onModel') === 'Link') checks.push(Boolean(this.link.label), Boolean(this.link.href))
      return checks.every(Boolean)
    },
  },
  methods: {
    onReferenceInput() {
      this.$refs.resource.close()
    },
    onClose() {
      this.$emit('close')
      this.$emit('update:show', false)
    },
    setLink() {
      return fetch('/api/flatfinder/link', {
        method: 'POST',
        body: JSON.stringify(this.link),
      })
        .then(res => res.json())
        .then(link => {
          this.form.reference = { onModel: 'Link', ref: { id: link._id } }
          this.link = { label: null, href: null }
        })
    },
    async onSubmit() {
      if (this.$path('reference.onModel') === 'Link') this.promise = await this.setLink()

      this.$emit('submit', this.form)
      this.onClose()
    },
    setInitialData() {
      this.link = { label: null, href: null }

      this.form = mergeDeepRight(structuredClone(DataTemplate), this.selected.item)
      if (this.$path('reference.onModel') === 'Link') {
        this.link = { label: this.$path('form.reference.ref.label'), href: this.$path('form.reference.ref.href') }
      }
    },
  },
  components: {
    ModalComponent,
    Card,
    SelectComponent,
    InputComponent,
    MediaComponent,
    ResourceSelect,
    RichText,
  },
}
</script>

<style lang="scss">
.flatfinder-settings {
  &__modal {
    width: 100%;
    max-width: 600px;
  }
}
</style>
